import React, { useState, useMemo } from 'react';
import { DatePicker, Select, Table, Card, Row, Col, Typography, Spin, Alert, Modal, Menu } from 'antd';
import { AArrowDown, Download } from 'lucide-react';

import { baseUrl } from '../../../baseUrl';
import axios from 'axios';
import jsPDF from 'jspdf';

const { Title } = Typography;
const { Option } = Select;

const categories = [
  { value: 'Mess Fee', label: 'Mess Fee' },
  { value: 'Hostel Admission Fee', label: 'Hostel Admission Fee' },
];

const PaymentReportPage = () => {
  const [date, setDate] = useState(null);
  const [category, setCategory] = useState('Mess Fee');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedFeeDetails, setSelectedFeeDetails] = useState(null);

  const showFeeDetails = (feeDetails) => {
    setSelectedFeeDetails(feeDetails);
    setModalVisible(true);
  };

  const formatDate = (date) => {
    return date.format('DD-MM-YYYY');
  };

  const formatTime = () => {
    return new Date().toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const downloadAsPDF = () => {
    if (!data.length || !date) {
      alert('No data available to generate PDF');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Set font
    doc.setFont('helvetica');

    // Title
    doc.setFontSize(20);
    doc.setTextColor(51, 51, 51);
    doc.text('Daily Payment Report', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Subtitle
    doc.setFontSize(16);
    doc.setTextColor(102, 102, 102);
    const subtitle = category === 'Mess Fee' ? 'Mess Fee Summary' : 'Hostel Admission Fee Summary';
    doc.text(subtitle, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Line separator
    doc.setLineWidth(0.5);
    doc.setDrawColor(51, 51, 51);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Report details
    doc.setFontSize(12);
    doc.setTextColor(51, 51, 51);
    doc.setFont('helvetica', 'bold');
    doc.text('Report Date:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(102, 102, 102);
    doc.text(formatDate(date), margin + 30, yPosition);
    yPosition += 8;

    doc.setTextColor(51, 51, 51);
    doc.setFont('helvetica', 'bold');
    doc.text('Generated On:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(102, 102, 102);
    doc.text(formatTime(), margin + 35, yPosition);
    yPosition += 8;

    doc.setTextColor(51, 51, 51);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Records:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(102, 102, 102);
    doc.text(data.length.toString(), margin + 35, yPosition);
    yPosition += 20;

    // Summary box background
    doc.setFillColor(248, 249, 250);
    doc.rect(margin, yPosition, pageWidth - 2 * margin, 80, 'F');
    doc.setDrawColor(233, 236, 239);
    doc.rect(margin, yPosition, pageWidth - 2 * margin, 80, 'S');
    yPosition += 15;

    // Summary title
    doc.setFontSize(14);
    doc.setTextColor(51, 51, 51);
    doc.setFont('helvetica', 'bold');
    const summaryTitle = category === 'Mess Fee' ? 'Payment Summary' : 'Fee Collection Summary';
    doc.text(summaryTitle, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    if (category === 'Mess Fee') {
      const totalRent = data.reduce((sum, record) => sum + parseFloat(record.rent_paid || 0), 0);
      const totalFine = data.reduce((sum, record) => sum + parseFloat(record.fine_paid || 0), 0);
      const totalOther = data.reduce((sum, record) => sum + parseFloat(record.other || 0), 0);
      const grandTotal = totalRent + totalFine + totalOther;

      // Summary items
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(73, 80, 87);

      // Rent
      doc.text('Rent Collected:', margin + 10, yPosition);
      doc.setTextColor(40, 167, 69);
      doc.text(`₹${totalRent.toFixed(2)}`, pageWidth - margin - 10, yPosition, { align: 'right' });
      yPosition += 8;

      // Fine
      doc.setTextColor(73, 80, 87);
      doc.text('Fine Collected:', margin + 10, yPosition);
      doc.setTextColor(220, 53, 69);
      doc.text(`₹${totalFine.toFixed(2)}`, pageWidth - margin - 10, yPosition, { align: 'right' });
      yPosition += 8;

      // Other
      doc.setTextColor(73, 80, 87);
      doc.text('Other Payments:', margin + 10, yPosition);
      doc.setTextColor(108, 117, 125);
      doc.text(`₹${totalOther.toFixed(2)}`, pageWidth - margin - 10, yPosition, { align: 'right' });
      yPosition += 12;

      // Total line
      doc.setLineWidth(0.5);
      doc.setDrawColor(51, 51, 51);
      doc.line(margin + 10, yPosition, pageWidth - margin - 10, yPosition);
      yPosition += 8;

      // Grand total
      doc.setFontSize(14);
      doc.setTextColor(51, 51, 51);
      doc.text('Grand Total:', margin + 10, yPosition);
      doc.setTextColor(0, 123, 255);
      doc.text(`₹${grandTotal.toFixed(2)}`, pageWidth - margin - 10, yPosition, { align: 'right' });

    } else if (category === 'Hostel Admission Fee') {
      const totalAmount = data.reduce((sum, record) => sum + parseFloat(record.total_amount || 0), 0);

      // Calculate totals for each fee type
      const feeTypeTotals = {};
      data.forEach(record => {
        if (record.fee_details?.fees) {
          record.fee_details.fees.forEach(fee => {
            if (fee.fee_id == 12 || fee.fee_id == 11) {
              const amount = parseFloat(fee.amount || fee.value || 0);
              feeTypeTotals["Caution Deposit"] = (feeTypeTotals["Caution Deposit"] || 0) + amount;
            } else {
              const amount = parseFloat(fee.amount || fee.value || 0);
              feeTypeTotals[fee.fee_name] = (feeTypeTotals[fee.fee_name] || 0) + amount;
            }
          });
        }
      });

      // Fee breakdown
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(73, 80, 87);

      Object.entries(feeTypeTotals).forEach(([feeName, amount]) => {
        doc.text(`${feeName}:`, margin + 10, yPosition);
        doc.setTextColor(40, 167, 69);
        doc.text(`₹${amount.toFixed(2)}`, pageWidth - margin - 10, yPosition, { align: 'right' });
        doc.setTextColor(73, 80, 87);
        yPosition += 8;
      });

      yPosition += 4;

      // Total line
      doc.setLineWidth(0.5);
      doc.setDrawColor(51, 51, 51);
      doc.line(margin + 10, yPosition, pageWidth - margin - 10, yPosition);
      yPosition += 8;

      // Grand total
      doc.setFontSize(14);
      doc.setTextColor(51, 51, 51);
      doc.text('Grand Total:', margin + 10, yPosition);
      doc.setTextColor(0, 123, 255);
      doc.text(`₹${totalAmount.toFixed(2)}`, pageWidth - margin - 10, yPosition, { align: 'right' });
    }

    // Footer
    yPosition = pageHeight - 30;
    doc.setFontSize(10);
    doc.setTextColor(108, 117, 125);
    doc.setFont('helvetica', 'normal');
    doc.text('This is a system generated report', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    doc.text('Generated by Payment Management System', pageWidth / 2, yPosition, { align: 'center' });

    // Save the PDF
    const filename = `payment-report-${category.toLowerCase().replace(/\s+/g, '-')}-${date?.format('DD-MM-YYYY')}.pdf`;
    doc.save(filename);
  };

  const formatFeeDetailsForCSV = (feeDetails) => {
    if (!feeDetails?.fees) return '';

    const feeStrings = feeDetails.fees.map(fee =>
      `${fee.fee_name}: ₹${parseFloat(fee.amount || fee.value).toFixed(2)}`
    );

    // Join all fees with semicolons to avoid comma conflicts in CSV
    return `"${feeStrings.join('; ')}"`;
  };

  const downloadAsList = () => {
    let csvContent = '';

    // Add headers
    const headers = currentColumns.map(col => col.title).join(',');
    csvContent += headers + '\n';

    // Add data rows
    data.forEach((row, index) => {
      const rowData = currentColumns.map(col => {
        if (col.dataIndex === 'fee_details') {
          return formatFeeDetailsForCSV(row.fee_details);
        }
        if (col.dataIndex) {
          return row[col.dataIndex] || '';
        }
        if (col.key === 'index') {
          return index + 1;
        }
        if (col.key === 'total_amount') {
          const total = parseFloat(row.rent_paid || 0) +
            parseFloat(row.fine_paid || 0) +
            parseFloat(row.other || 0);
          return total.toFixed(2);
        }
        return '';
      }).join(',');
      csvContent += rowData + '\n';
    });

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payment-report-${date?.format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = useMemo(() => ({
    'Mess Fee': [
      {
        title: 'Sl.No',
        key: 'index',
        fixed: 'left',
        width: 80,
        render: (_, __, index) => index + 1
      },
      {
        title: 'Receipt No',
        dataIndex: 'receipt_number',
        key: 'receipt_number',
        fixed: 'left',
        width: 150
      },
      {
        title: 'College Admission No',
        dataIndex: 'college_admission_no',
        key: 'college_admission_no',
        width: 150
      },
      {
        title: 'Hostel Admission No',
        dataIndex: 'hostel_admission_no',
        key: 'hostel_admission_no',
        width: 150
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 150
      },
      {
        title: 'Rent Paid',
        dataIndex: 'rent_paid',
        key: 'rent_paid',
        width: 120,
        render: value => value ? `₹${parseFloat(value).toFixed(2)}` : '-'
      },
      {
        title: 'Fine Paid',
        dataIndex: 'fine_paid',
        key: 'fine_paid',
        width: 120,
        render: value => value ? `₹${parseFloat(value).toFixed(2)}` : '-'
      },
      {
        title: 'Other',
        dataIndex: 'other',
        key: 'other',
        width: 120,
        render: value => value ? `₹${parseFloat(value).toFixed(2)}` : '-'
      },
      {
        title: 'Total Amount',
        key: 'total_amount',
        width: 120,
        render: (_, record) => {
          const total = parseFloat(record.rent_paid || 0) +
            parseFloat(record.fine_paid || 0) +
            parseFloat(record.other || 0);
          return `₹${total.toFixed(2)}`;
        }
      }
    ],
    'Hostel Admission Fee': [
      {
        title: 'Sl.No',
        key: 'index',
        fixed: 'left',
        width: 80,
        render: (_, __, index) => index + 1
      },
      {
        title: 'Receipt No',
        dataIndex: 'receipt_number',
        key: 'receipt_number',
        fixed: 'left',
        width: 150
      },
      {
        title: 'College Admission No',
        dataIndex: 'college_admission_no',
        key: 'college_admission_no',
        width: 150
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 150
      },
      {
        title: 'Total Amount',
        dataIndex: 'total_amount',
        key: 'total_amount',
        width: 120,
        render: value => `₹${parseFloat(value).toFixed(2)}`
      },
      {
        title: 'Fee Details',
        dataIndex: 'fee_details',
        key: 'fee_details',
        width: 150,
        render: (feeDetails) => {
          if (!feeDetails?.fees) return '-';
          return (
            <button
              onClick={() => showFeeDetails(feeDetails)}
              className="px-4 py-2 text-blue-600 hover:text-blue-800 font-medium rounded-md hover:bg-blue-50 transition-colors"
            >
              View Details
            </button>
          );
        }
      }
    ]
  }), []);

  const handleDateChange = (date) => {
    setDate(date);
    fetchData(date, category);
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    if (date) {
      fetchData(date, value);
    }
  };

  const fetchData = async (selectedDate, selectedCategory) => {
    if (!selectedDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${baseUrl}/clerk-a3/daily-report`, {
        date: selectedDate.format('YYYY-MM-DD'),
        category: selectedCategory
      });

      if (!response.data) throw new Error('Failed to fetch data');

      setData(response.data);
    } catch (err) {
      setError('Failed to load report data. Please try again.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const currentColumns = useMemo(() => {
    if (category === 'Rent' || category === 'Fine' || category === 'Other') {
      return columns['Mess Fee'];
    }
    return columns[category] || [];
  }, [category, columns]);

  const renderSummary = (pageData) => {
    if (!pageData.length) return null;

    if (category === 'Mess Fee') {
      const totalRent = pageData.reduce((sum, record) => sum + parseFloat(record.rent_paid || 0), 0);
      const totalFine = pageData.reduce((sum, record) => sum + parseFloat(record.fine_paid || 0), 0);
      const totalOther = pageData.reduce((sum, record) => sum + parseFloat(record.other || 0), 0);
      const grandTotal = totalRent + totalFine + totalOther;

      return (
        <div className="bg-gray-50 border-t border-gray-200">
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-gray-700 font-medium">Rent</div>
              <div className="text-right font-medium">₹{totalRent.toFixed(2)}</div>

              <div className="text-gray-700 font-medium">Fine</div>
              <div className="text-right font-medium">₹{totalFine.toFixed(2)}</div>

              <div className="text-gray-700 font-medium">Other</div>
              <div className="text-right font-medium">₹{totalOther.toFixed(2)}</div>

              <div className="text-gray-900 font-semibold pt-2 border-t">Total</div>
              <div className="text-right font-semibold pt-2 border-t">₹{grandTotal.toFixed(2)}</div>
            </div>
            <div className="text-sm text-gray-600 mt-2">
              Total {pageData.length} record{pageData.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      );
    } else if (category === 'Hostel Admission Fee') {
      const totalAmount = pageData.reduce((sum, record) => sum + parseFloat(record.total_amount || 0), 0);

      // Calculate totals for each fee type
      const feeTypeTotals = {};
      pageData.forEach(record => {
        if (record.fee_details?.fees) {
          record.fee_details.fees.forEach(fee => {
            if (fee.fee_id == 12 || fee.fee_id == 11) {
              const amount = parseFloat(fee.amount || fee.value || 0);
              feeTypeTotals["Caution Deposit"] = (feeTypeTotals["Caution Deposit"] || 0) + amount;
            } else {
              const amount = parseFloat(fee.amount || fee.value || 0);
              feeTypeTotals[fee.fee_name] = (feeTypeTotals[fee.fee_name] || 0) + amount;
            }
          });
        }
      });

      return (
        <div className="bg-gray-50 border-t border-gray-200">
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(feeTypeTotals).map(([feeName, amount]) => {
                return (
                  <React.Fragment key={feeName}>
                    <div className="text-gray-700 font-medium">{feeName}</div>
                    <div className="text-right font-medium">₹{amount.toFixed(2)}</div>
                  </React.Fragment>
                )
              })}

              <div className="text-gray-900 font-semibold pt-2 border-t">Total</div>
              <div className="text-right font-semibold pt-2 border-t">₹{totalAmount.toFixed(2)}</div>
            </div>
            <div className="text-sm text-gray-600 mt-2">
              Total {pageData.length} record{pageData.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="w-11/12 overflow-auto">
      <Card className="shadow-sm">
        <div className="mb-6 flex items-center">
          <Title level={4} className="m-0">Daily Payment Report</Title>
        </div>

        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} md={8}>
            <div className="flex flex-col">
              <label className="mb-2 font-medium">Select Date</label>
              <DatePicker
                className="w-full"
                onChange={handleDateChange}
                format="DD-MM-YYYY"
                placeholder="Select date"
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="flex flex-col">
              <label className="mb-2 font-medium">Payment Category</label>
              <Select
                className="w-full"
                value={category}
                onChange={handleCategoryChange}
                placeholder="Select category"
              >
                {categories.map(cat => (
                  <Option key={cat.value} value={cat.value}>{cat.label}</Option>
                ))}
              </Select>
            </div>
          </Col>
        </Row>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            className="mb-4"
          />
        )}

        <div className="relative">
          <Table
            columns={currentColumns}
            dataSource={data}
            loading={loading}
            rowKey="receipt_number"
            scroll={{ x: 'max-content' }}
            pagination={false}
            footer={renderSummary}
          />
        </div>
        
        {data.length > 0 && (
          <div className="mt-6 flex justify-end space-x-4">
            <button
              onClick={downloadAsPDF}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </button>
            <button
              onClick={downloadAsList}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </button>
          </div>
        )}
      </Card>

      <Modal
        title="Fee Details"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <button
            key="close"
            onClick={() => setModalVisible(false)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        ]}
        width={500}
      >
        <div className="mt-4">
          {selectedFeeDetails?.fees?.map((fee, index) => (
            <div
              key={index}
              className="flex justify-between items-center py-3 border-b last:border-b-0"
            >
              <span className="text-gray-700 font-medium">{fee.fee_name}</span>
              <span className="text-gray-900">₹{parseFloat(fee.amount || fee.value).toFixed(2)}</span>
            </div>
          ))}
          <div className="mt-4 pt-3 border-t">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-800">Total</span>
              <span className="text-lg font-semibold text-gray-900">
                ₹{selectedFeeDetails?.fees?.reduce((sum, fee) =>
                  sum + parseFloat(fee.amount || fee.value), 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PaymentReportPage;